import type { PastAnswerNode } from './PastAnswers';
import type { GetAutoApplyEligiblePastAnswersQuery } from './__generated__/PastAnswers.generated';

/**
 * Mock data for auto apply eligible past answers
 * Covers various edge cases and question types for testing
 */

/**
 * Basic mock data with a few common questions
 */
export const basicMockPastAnswers: PastAnswerNode[] = [
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-1',
            type: 'SELECT' as const,
            question: 'Can you operate a forklift?',
            options: [
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Yes',
                    value: 'yes',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'No',
                    value: 'no',
                    attributeId: null,
                },
            ],
            intentId: 'intent-1',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Yes'],
                createdAt: '2026-01-04T00:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-2',
            type: 'TEXT' as const,
            question: 'What is your desired salary?',
            options: [],
            intentId: 'intent-2',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['$75,000/year'],
                createdAt: '2026-01-04T00:00:00Z',
            },
        ],
    },
];

/**
 * IQv2 hosted questions. These are blended into the results ONLY when the
 * questions API is queried with shouldIncludeHostedSQ=true (proctor
 * ia_afm_iqv2_prefill_tst in show_only / show_and_prefill). Used by the
 * IQv2 inclusion stories to verify the behavioral difference driven by the flag.
 * Shares basicMockPastAnswers' date so it groups under the same heading.
 */
export const iqv2HostedMockPastAnswers: PastAnswerNode[] = [
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-iqv2-1',
            type: 'TEXT' as const,
            question: 'Are you legally authorized to work in the United States?',
            options: [],
            intentId: 'intent-iqv2-1',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Yes'],
                createdAt: '2026-01-04T00:00:00Z',
            },
        ],
    },
];

/**
 * Questions whose text/option labels contain employer-authored HTML markup,
 * which must render as formatted markup rather than literal tags. Covers a
 * label with block + inline tags, a SELECT option with inline HTML, and an
 * XSS payload that must be sanitized away.
 */
export const htmlMockPastAnswers: PastAnswerNode[] = [
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-html-esign',
            type: 'TEXT' as const,
            question:
                '<b>E-SIGNATURE</b><br/><div>Please enter your name to e-sign.</div><br/>Full Name',
            options: [],
            intentId: 'intent-html-esign',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Jane Doe'],
                createdAt: '2026-03-01T00:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-html-select',
            type: 'SELECT' as const,
            question: 'Which <b>shift</b> do you prefer?',
            options: [
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: '<b>Morning</b> shift',
                    value: 'morning',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: '<b>Evening</b> shift',
                    value: 'evening',
                    attributeId: null,
                },
            ],
            intentId: 'intent-html-select',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['morning'],
                createdAt: '2026-03-01T00:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-html-xss',
            type: 'TEXT' as const,
            question: 'Confirm your name<script>window.__xss__ = true;</script>',
            options: [],
            intentId: 'intent-html-xss',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Confirmed'],
                createdAt: '2026-03-01T00:00:00Z',
            },
        ],
    },
];

/**
 * Mock data covering all 8 question types for type-aware rendering tests.
 * All entries share the same createdAt so they group under one date heading.
 */
export const allQuestionTypesMockPastAnswers: PastAnswerNode[] = [
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-select',
            type: 'SELECT' as const,
            question: "[SELECT] Do you have a valid driver's license?",
            options: [
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Yes',
                    value: 'yes',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'No',
                    value: 'no',
                    attributeId: null,
                },
            ],
            intentId: 'intent-type-select',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['yes'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-multiselect',
            type: 'MULTISELECT' as const,
            question: '[MULTISELECT] Which shifts can you work?',
            options: [
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Morning',
                    value: 'morning',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Afternoon',
                    value: 'afternoon',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Evening',
                    value: 'evening',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Weekends',
                    value: 'weekends',
                    attributeId: null,
                },
            ],
            intentId: 'intent-type-multiselect',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['morning', 'afternoon', 'weekends'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-text',
            type: 'TEXT' as const,
            question: '[TEXT] What is your desired salary?',
            options: [],
            intentId: 'intent-type-text',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['$75,000/year'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-textarea',
            type: 'TEXTAREA' as const,
            question: '[TEXTAREA] Describe your customer service experience.',
            options: [],
            intentId: 'intent-type-textarea',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: [
                    'I have over 5 years of experience in customer service roles, handling both in-person and phone interactions. I have successfully resolved numerous conflicts by actively listening to customer concerns, empathizing with their situation, and working collaboratively to find mutually beneficial solutions. My approach focuses on de-escalation and finding win-win outcomes. I pride myself on maintaining positive relationships with customers even in challenging situations.',
                ],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-date',
            type: 'DATE' as const,
            question: '[DATE] When can you start?',
            options: [],
            intentId: 'intent-type-date',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['03/15/2026'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-hierarchical',
            type: 'HIERARCHICAL' as const,
            question: '[HIERARCHICAL] Where are you located?',
            options: [],
            intentId: 'intent-type-hierarchical',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Texas', 'San Antonio', '78209'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-file',
            type: 'FILE' as const,
            question: '[FILE] Upload your certification.',
            options: [],
            intentId: 'intent-type-file',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['forklift-certification.pdf'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-information',
            type: 'INFORMATION' as const,
            question: '[INFORMATION] Please read the disclaimer before continuing.',
            options: [],
            intentId: 'intent-type-information',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: [],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-pagebreak',
            type: 'PAGEBREAK' as const,
            question: '[PAGEBREAK] Page separator.',
            options: [],
            intentId: 'intent-type-pagebreak',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: [],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: 'question-type-questionbank',
            type: 'QUESTION_BANK' as const,
            question: '[QUESTION_BANK] How many years of experience do you have?',
            options: [],
            intentId: 'intent-type-questionbank',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['5'],
                createdAt: '2026-02-20T10:00:00Z',
            },
        ],
    },
];

/**
 * Comprehensive mock data with edge cases
 * Sorted by date descending (most recent first)
 *
 * Matches real API behavior:
 * - All questions have empty `id: ''` (Apollo cache normalization edge case)
 * - Some questions share the same `intentId` (first two share one)
 * - SELECT answer values are labels (e.g. "Yes"), not option value IDs
 */
export const comprehensiveMockPastAnswers: PastAnswerNode[] = [
    // Question with recent timestamp (2026-02-15)
    // First two share intentId to match real API behavior
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'SELECT' as const,
            question: "Do you have a valid driver's license?",
            options: [
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Yes',
                    value: 'yes',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'No',
                    value: 'no',
                    attributeId: null,
                },
            ],
            intentId: 'intent-shared-1',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Yes'],
                createdAt: '2026-02-15T10:30:00Z',
            },
        ],
    },
    // SELECT type with options (2026-01-10) — shares intentId with first entry
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'SELECT' as const,
            question: 'Can you operate a forklift?',
            options: [
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'Yes',
                    value: 'yes',
                    attributeId: null,
                },
                {
                    __typename: 'AutoApplyEligibleQuestionOption',
                    label: 'No',
                    value: 'no',
                    attributeId: null,
                },
            ],
            intentId: 'intent-shared-1',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Yes'],
                createdAt: '2026-01-10T00:00:00Z',
            },
        ],
    },
    // TEXT type with short answer (2026-01-10)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question: 'What is your desired salary?',
            options: [],
            intentId: 'intent-text-1',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['$75,000/year'],
                createdAt: '2026-01-10T00:00:00Z',
            },
        ],
    },
    // TEXT type with long answer (2026-01-10)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question:
                'Please describe your experience with customer service and conflict resolution.',
            options: [],
            intentId: 'intent-text-2',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: [
                    'I have over 5 years of experience in customer service roles, handling both in-person and phone interactions. I have successfully resolved numerous conflicts by actively listening to customer concerns, empathizing with their situation, and working collaboratively to find mutually beneficial solutions. My approach focuses on de-escalation and finding win-win outcomes.',
                ],
                createdAt: '2026-01-10T00:00:00Z',
            },
        ],
    },
    // Long question with short answer (2026-01-10)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question:
                'Are you related to anyone (familial or by marriage) who is currently working for Paladin Security Group (PSG) or Paladin Airport Security Services (PASS)? If yes, please provide their name(s) and relationship.',
            options: [],
            intentId: 'intent-text-5',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['No'],
                createdAt: '2026-01-10T00:00:00Z',
            },
        ],
    },
    // Multiple values (comma-separated in single string) (2026-01-10)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question: 'What days are you available to work?',
            options: [],
            intentId: 'intent-text-6',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Monday, Tuesday, Wednesday, Friday, Saturday'],
                createdAt: '2026-01-10T00:00:00Z',
            },
        ],
    },
    // SELECT type without options (answer provided as text) (2026-01-05)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'SELECT' as const,
            question: 'What is your current employment status?',
            options: [],
            intentId: 'intent-select-2',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['Working part-time'],
                createdAt: '2026-01-05T00:00:00Z',
            },
        ],
    },
    // TEXT type with numeric answer (2026-01-05)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question: 'How many years of management experience do you have?',
            options: [],
            intentId: 'intent-text-3',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['3'],
                createdAt: '2026-01-05T00:00:00Z',
            },
        ],
    },
    // TEXT type with special characters (2026-01-05)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question: 'What programming languages & frameworks do you know?',
            options: [],
            intentId: 'intent-text-4',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ['JavaScript, TypeScript, React, Node.js, Python & C++'],
                createdAt: '2026-01-05T00:00:00Z',
            },
        ],
    },
    // Question with old timestamp (2026-01-05)
    {
        __typename: 'AutoApplyEligiblePastAnswer',
        question: {
            __typename: 'AutoApplyEligibleQuestion',
            id: '',
            type: 'TEXT' as const,
            question: 'What is your highest level of education?',
            options: [],
            intentId: 'intent-text-7',
        },
        answers: [
            {
                __typename: 'AutoApplyPastAnswerValue',
                values: ["Bachelor's Degree"],
                createdAt: '2026-01-05T00:00:00Z',
            },
        ],
    },
];

/**
 * Mock data for pagination testing (27 questions to span 3 pages)
 */
export const paginationMockPastAnswers: PastAnswerNode[] = Array.from({ length: 27 }, (_, i) => ({
    __typename: 'AutoApplyEligiblePastAnswer' as const,
    question: {
        __typename: 'AutoApplyEligibleQuestion' as const,
        id: `question-pagination-${i + 1}`,
        type: 'TEXT' as const,
        question: `Sample question ${i + 1}?`,
        options: [],
        intentId: `intent-pagination-${i + 1}`,
    },
    answers: [
        {
            __typename: 'AutoApplyPastAnswerValue' as const,
            values: [`Answer ${i + 1}`],
            createdAt: '2026-01-04T00:00:00Z',
        },
    ],
}));

/**
 * Mock data for exactly 10 questions (boundary case - no pagination)
 */
export const tenQuestionsMockPastAnswers: PastAnswerNode[] = Array.from({ length: 10 }, (_, i) => ({
    __typename: 'AutoApplyEligiblePastAnswer' as const,
    question: {
        __typename: 'AutoApplyEligibleQuestion' as const,
        id: `question-ten-${i + 1}`,
        type: 'TEXT' as const,
        question: `Question ${i + 1}?`,
        options: [],
        intentId: `intent-ten-${i + 1}`,
    },
    answers: [
        {
            __typename: 'AutoApplyPastAnswerValue' as const,
            values: [`Answer ${i + 1}`],
            createdAt: '2026-01-04T00:00:00Z',
        },
    ],
}));

/**
 * Mock data for many pages (100 questions = 10 pages at 10 per page)
 * Used to test ellipsis pagination controls
 */
export const manyPagesMockPastAnswers: PastAnswerNode[] = Array.from({ length: 100 }, (_, i) => ({
    __typename: 'AutoApplyEligiblePastAnswer' as const,
    question: {
        __typename: 'AutoApplyEligibleQuestion' as const,
        id: `question-many-${i + 1}`,
        type: 'TEXT' as const,
        question: `Sample question ${i + 1}?`,
        options: [],
        intentId: `intent-many-${i + 1}`,
    },
    answers: [
        {
            __typename: 'AutoApplyPastAnswerValue' as const,
            values: [`Answer ${i + 1}`],
            createdAt: '2026-01-04T00:00:00Z',
        },
    ],
}));

/**
 * Type for the connection structure returned by autoApplyEligiblePastAnswers
 */
type PastAnswersConnection = NonNullable<
    GetAutoApplyEligiblePastAnswersQuery['autoApplyEligiblePastAnswers']
>;

/**
 * Creates a mock Relay-style connection from a flat array of PastAnswerNode items.
 * Supports cursor-based pagination (first/after).
 *
 * @param allItems - Full array of mock past answer nodes
 * @param first - Number of items to return (default: 20)
 * @param after - Cursor to start after (base64-encoded index)
 */
export const createMockPastAnswersConnection = (
    allItems: PastAnswerNode[],
    first = 20,
    after?: string | null,
): PastAnswersConnection => {
    // Decode cursor to get offset (cursor is base64 of "cursor:<index>")
    let startIndex = 0;
    if (after) {
        try {
            const decoded = atob(after);
            const index = parseInt(decoded.replace('cursor:', ''), 10);
            startIndex = index + 1;
        } catch {
            startIndex = 0;
        }
    }

    const slicedItems = allItems.slice(startIndex, startIndex + first);
    const endIndex = startIndex + slicedItems.length - 1;
    const hasNextPage = startIndex + first < allItems.length;

    return {
        __typename: 'AutoApplyEligiblePastAnswersConnection',
        edges: slicedItems.map((node, i) => ({
            __typename: 'AutoApplyEligiblePastAnswerEdge' as const,
            cursor: btoa(`cursor:${startIndex + i}`),
            node,
        })),
        pageInfo: {
            __typename: 'PageInfo',
            hasNextPage,
            hasPreviousPage: startIndex > 0,
            startCursor: slicedItems.length > 0 ? btoa(`cursor:${startIndex}`) : null,
            endCursor: slicedItems.length > 0 ? btoa(`cursor:${endIndex}`) : null,
        },
        totalCount: allItems.length,
    };
};
